import React, { forwardRef, useCallback } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';

import { getFieldError } from 'helpers/formik';
import useMask from 'hooks/useMask';

const TimeComponent = forwardRef(
  (
    {
      field,
      form,
      fullWidth,
      name,
      value,
      errorMessageProp,
      disabled,
      loading,
      helperText,
      startAdornment,
      endAdornment,
      onChange,
      onBlur,
      mask,
      defaultValue = '00:00',
      ...props
    },
    ref,
  ) => {
    // Set the default name and value
    name = field?.name ?? name;
    value = field?.value ?? value ?? defaultValue;

    const { maskedValue, maskClean } = useMask(mask, value);

    // onChange handler to update the field value
    const onChangeHandler = useCallback(
      (e) => {
        const newValue = e.target.value;
        form && form.setFieldValue(field.name, maskClean(newValue));
        onChange && onChange(e);
      },
      [form, field, maskClean, onChange],
    );

    // onBlur handler to clean the value and update the field
    const onBlurHandler = useCallback(
      (e) => {
        const v = e.target.value;
        form?.setFieldTouched(field.name, true);
        if (!v) return;
        form?.setFieldValue(field.name, maskClean(v));
        onBlur && onBlur(e);
      },
      [form, field, maskClean, onBlur],
    );

    // InputProps using memoization to prevent unnecessary re-renders
    const InputProps = React.useMemo(() => {
      let end = null;
      let start = null;

      if (startAdornment) {
        start = <InputAdornment position='start'>{startAdornment}</InputAdornment>;
      }

      if (endAdornment) {
        end = <InputAdornment position='end'>{endAdornment}</InputAdornment>;
      }

      if (loading) {
        end = (
          <InputAdornment position='end'>
            <CircularProgress color='secondary' size={20} />
          </InputAdornment>
        );
      }

      return {
        endAdornment: end,
        startAdornment: start,
      };
    }, [loading, endAdornment, startAdornment]);

    // Retrieve error message
    const errorMessage = errorMessageProp || (form && getFieldError(field?.name, form));
    const hasError = !!errorMessage;

    return (
      <TextField
        classes={props.classes}
        fullWidth={fullWidth ?? true}
        helperText={errorMessage ?? helperText}
        disabled={form?.isSubmitting || disabled || loading}
        error={hasError}
        name={name}
        type='time'
        value={maskedValue}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        InputProps={InputProps}
        inputRef={ref}
        placeholder=''
        {...props}
      />
    );
  },
);

TimeComponent.displayName = 'Time';

export const Time = React.memo(TimeComponent);
